/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { LayerManager, LayerViewState } from '../../services/layerManager';
import { UndergroundLayer } from '../../types/underground';
import { getLayerTickMetrics } from '../../utils/undergroundTick';
import { playUiSfx } from '../../utils/uiAudio';
import { resolveSwitchUndergroundLayer } from '../../src/adapters/ui/hooks/useLayerSwitch';
import './LayerControlPanel.css';

interface LayerControlPanelProps {
  layerManager: LayerManager;
  onLayerChange?: (layer: UndergroundLayer) => void;
  onVisibilityChange?: (visibility: any) => void;
}

interface LayerButtonProps {
  layer: UndergroundLayer;
  isActive: boolean;
  isVisible: boolean;
  opacity: number;
  config: any;
  onSelect: (layer: UndergroundLayer) => void;
  onToggleVisibility: (layer: UndergroundLayer) => void;
  onOpacityChange: (layer: UndergroundLayer, opacity: number) => void;
}

const LayerButton: React.FC<LayerButtonProps> = ({
  layer,
  isActive,
  isVisible,
  opacity,
  config,
  onSelect,
  onToggleVisibility,
  onOpacityChange
}) => {
  const getLayerIcon = (layer: UndergroundLayer): string => {
    switch (layer) {
      case UndergroundLayer.Surface:
        return '🏢';
      case UndergroundLayer.Infrastructure:
        return '⚙️';
      case UndergroundLayer.Connectivity:
        return '🔌';
      default:
        return '📍';
    }
  };

  const getLayerName = (layer: UndergroundLayer): string => {
    switch (layer) {
      case UndergroundLayer.Surface:
        return 'Surface';
      case UndergroundLayer.Infrastructure:
        return 'Infrastructure';
      case UndergroundLayer.Connectivity:
        return 'Connectivity';
      default:
        return 'Unknown';
    }
  };

  const layerTestId =
    layer === UndergroundLayer.Surface
      ? 'surface'
      : layer === UndergroundLayer.Infrastructure
        ? 'infrastructure'
        : 'connectivity';

  return (
    <div
      className={`layer-button ${isActive ? 'active' : ''}`}
      data-testid={`layer-select-${layerTestId}`}
      onClick={() => onSelect(layer)}
    >
      <div className="layer-header">
        <span className="layer-icon">{getLayerIcon(layer)}</span>
        <span className="layer-name">{getLayerName(layer)}</span>
      </div>

      <div className="layer-controls">
        <button
          className={`visibility-toggle ${isVisible ? 'visible' : 'hidden'}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(layer);
          }}
          title={isVisible ? 'Hide layer' : 'Show layer'}
        >
          {isVisible ? '👁️' : '👁️‍🗨️'}
        </button>

        {isVisible && (
          <div className="opacity-control">
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => onOpacityChange(layer, parseFloat(e.target.value))}
              className="opacity-slider"
              title={`Opacity: ${Math.round(opacity * 100)}%`}
            />
          </div>
        )}
      </div>

      <div
        className="layer-indicator"
        style={{
          backgroundColor: config?.color || '#888',
          opacity: opacity
        }}
      />
    </div>
  );
};

export const LayerControlPanel: React.FC<LayerControlPanelProps> = ({
  layerManager,
  onLayerChange,
  onVisibilityChange
}) => {
  const [viewState, setViewState] = useState<LayerViewState>(layerManager.getCurrentState());
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const unsubscribe = layerManager.subscribe((newState) => {
      setViewState((prev) => {
        if (onLayerChange && newState.currentLayer !== prev.currentLayer) {
          onLayerChange(newState.currentLayer);
        }
        return newState;
      });
      setIsTransitioning(newState.isTransitioning);
      onVisibilityChange?.(newState.visibility);
    });

    return () => unsubscribe();
  }, [layerManager, onLayerChange, onVisibilityChange]);

  const handleLayerSelect = (layer: UndergroundLayer) => {
    if (!isTransitioning) {
      playUiSfx('click');
      resolveSwitchUndergroundLayer(layer, 1000);
    }
  };

  const handleVisibilityToggle = (layer: UndergroundLayer) => {
    const isVisible = layerManager.isLayerVisible(layer);
    layerManager.setLayerVisibility(layer, !isVisible);
  };

  const handleOpacityChange = (layer: UndergroundLayer, opacity: number) => {
    layerManager.setLayerTransparency(layer, opacity);
  };

  const getCurrentLayerInfo = () => {
    const layerInfo = layerManager.getLayerInfo(viewState.currentLayer);
    return layerInfo || { name: 'Unknown Layer', description: '' };
  };

  const currentLayerInfo = getCurrentLayerInfo();
  const allLayers = layerManager.getAllLayers();
  const tickBonus = getLayerTickMetrics(viewState.currentLayer);

  return (
    <div
      className={`layer-control-panel${collapsed ? ' collapsed' : ''}`}
      data-testid="layer-control-panel"
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="layer-panel-header">
        <h3>Layer View</h3>
        <button
          type="button"
          className="layer-panel-toggle"
          data-testid="layer-panel-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand layer panel' : 'Collapse layer panel'}
        >
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>

      {/* Layer Selection — always visible */}
      <div className="layer-selection">
        <div className="layer-buttons">
          {allLayers.map(layer => (
            <LayerButton
              key={layer}
              layer={layer}
              isActive={viewState.currentLayer === layer}
              isVisible={layerManager.isLayerVisible(layer)}
              opacity={viewState.visibility.transparency[
                layer === UndergroundLayer.Surface ? 'surface' :
                layer === UndergroundLayer.Infrastructure ? 'underground1' : 'underground2'
              ]}
              config={layerManager.getLayerInfo(layer)}
              onSelect={handleLayerSelect}
              onToggleVisibility={handleVisibilityToggle}
              onOpacityChange={handleOpacityChange}
            />
          ))}
        </div>

        {isTransitioning && (
          <div className="transition-indicator">
            <div className="loading-spinner" />
            <span>Switching layers...</span>
          </div>
        )}
      </div>

      {!collapsed && (
      <div className="layer-panel-body">
      {/* Current Layer Info */}
      <div className="current-layer-info">
        <h4>Current Layer</h4>
        <div className="layer-details">
          <h5>{currentLayerInfo.name}</h5>
          <p>{currentLayerInfo.description}</p>

          {tickBonus && (
            <p className="layer-tick-bonus">
              Passive bonus while active: +{tickBonus.complianceBonus} compliance/tick, −{tickBonus.threatReduction} threat/tick
            </p>
          )}

          <div className="layer-stats">
            <div className="stat">
              <label>Zoom:</label>
              <span>{viewState.zoom.toFixed(1)}x</span>
            </div>
            <div className="stat">
              <label>Camera Z:</label>
              <span>{viewState.cameraPosition.z}</span>
            </div>
            <div className="stat">
              <label>Visible Layers:</label>
              <span>{layerManager.getVisibleLayers().length}/{allLayers.length}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="layer-quick-presets">
        <h4>Quick Views</h4>
        <div className="preset-views">
          <button
            onClick={() => {
              layerManager.setLayerVisibility(UndergroundLayer.Surface, true);
              layerManager.setLayerVisibility(UndergroundLayer.Infrastructure, true);
              layerManager.setLayerVisibility(UndergroundLayer.Connectivity, true);
              layerManager.setLayerTransparency(UndergroundLayer.Surface, 0.7);
              layerManager.setLayerTransparency(UndergroundLayer.Infrastructure, 0.8);
              layerManager.setLayerTransparency(UndergroundLayer.Connectivity, 0.9);
            }}
            disabled={isTransitioning}
          >
            👻 X-Ray View
          </button>
          <button
            onClick={() => {
              allLayers.forEach(layer => {
                layerManager.setLayerVisibility(layer, true);
                layerManager.setLayerTransparency(layer, 1.0);
              });
              resolveSwitchUndergroundLayer(UndergroundLayer.Surface, 300);
            }}
            disabled={isTransitioning}
          >
            🏢 Surface Only
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h4>Quick Actions</h4>
        <button
          onClick={() => {
            allLayers.forEach(layer => {
              layerManager.setLayerVisibility(layer, false);
            });
            resolveSwitchUndergroundLayer(viewState.currentLayer, 300);
          }}
          disabled={isTransitioning}
        >
          👁️ Show Only Current
        </button>
        <button
          onClick={() => {
            allLayers.forEach(layer => {
              layerManager.setLayerVisibility(layer, true);
              layerManager.setLayerTransparency(layer, 1.0);
            });
          }}
          disabled={isTransitioning}
        >
          👁️ Show All Layers
        </button>
        <button
          onClick={() => {
            layerManager.setCameraPosition({ x: 7, y: 7, z: viewState.cameraPosition.z });
            layerManager.setCameraRotation({ x: -45, y: 0, z: 0 });
            layerManager.setZoom(1.0);
          }}
          disabled={isTransitioning}
        >
          🎯 Reset View
        </button>
      </div>
      </div>
      )}
    </div>
  );
};